// Static league identity used to render leagues the user hasn't joined yet (so the
// "Join a league" list shows all configured competitions even before any Firestore
// league docs are read). Identity matches the shared LEAGUE_DEFS — the same source
// the backend writes — so the UI never drifts from the real leagues. Live state
// (membership, tier, rank, member counts, rounds) comes from Firestore at runtime.
import { LEAGUE_DEFS, LEAGUE_KEYS, type LeagueKey, type Sport } from '@h2h/shared';

export interface LeagueIdentity {
  leagueId: LeagueKey;
  name: string;
  country: string;
  shortCode: string;
  sport: Sport;
}

export const LEAGUE_IDENTITIES: LeagueIdentity[] = LEAGUE_KEYS.map((key) => {
  const def = LEAGUE_DEFS[key];
  return {
    leagueId: def.leagueId,
    name: def.name,
    country: def.country,
    shortCode: def.shortCode,
    sport: def.sport
  };
});

export function identityFor(leagueId: string): LeagueIdentity | undefined {
  return LEAGUE_IDENTITIES.find((l) => l.leagueId === leagueId);
}

export const FOOTBALL_LEAGUE_COUNT = LEAGUE_IDENTITIES.length;

// ── App focus ─────────────────────────────────────────────────────────────────
// For now the only competition actually in play is the World Cup — the domestic
// leagues are between seasons. We focus the whole app on the World Cup and show
// the rest as "Waiting" until their next season starts. Flip a league into
// `ACTIVE_LEAGUE_IDS` (or change FOCUSED_LEAGUE_ID) to bring it back online.

/** The single competition the app opens to and centres everything around. */
export const FOCUSED_LEAGUE_ID: LeagueKey = 'world-cup-2026';

/** Leagues that are live and playable right now. Everything else is "Waiting". */
const ACTIVE_LEAGUE_IDS = new Set<string>([FOCUSED_LEAGUE_ID]);

/** A cycling race league (id minted by shared `cyclingLeagueId`: `cycling-<slug>-<year>`).
 *  Cycling leagues are created dynamically by the backend (not in the static football
 *  LEAGUE_DEFS), so the lobby/play gates recognise them by this id shape. */
export function isCyclingLeagueId(leagueId: string | undefined | null): boolean {
  return !!leagueId && leagueId.startsWith('cycling-');
}

/** Is this league live and playable right now (vs. between seasons)? Cycling races
 *  are always considered playable (their lifecycle is driven by the live race window
 *  on the backend — a race with no open stage simply shows "no open round"). */
export function isLeagueActive(leagueId: string | undefined | null): boolean {
  return !!leagueId && (ACTIVE_LEAGUE_IDS.has(leagueId) || isCyclingLeagueId(leagueId));
}

/** The active competitions, in display order (just the World Cup for now). */
export const ACTIVE_LEAGUE_IDENTITIES = LEAGUE_IDENTITIES.filter((l) => isLeagueActive(l.leagueId));

/** The competitions on a break, shown as "Waiting". */
export const WAITING_LEAGUE_IDENTITIES = LEAGUE_IDENTITIES.filter((l) => !isLeagueActive(l.leagueId));

// Approximate kick-off of each waiting league's next campaign (2026/27), as epoch
// ms. Used to drive the "starts in …" countdowns on the home screen. These are
// best-known season-opener dates; refine when the official fixture lists drop.
const SEASON_START_AT: Partial<Record<LeagueKey, number>> = {
  superliga: Date.UTC(2026, 6, 17, 17, 0, 0), // Danish Superliga — mid-July
  'premier-league': Date.UTC(2026, 7, 15, 11, 30, 0), // ~15 Aug
  'la-liga': Date.UTC(2026, 7, 15, 16, 0, 0), // ~15 Aug
  'serie-a': Date.UTC(2026, 7, 22, 16, 0, 0) // ~22 Aug
};

/** When a waiting league's next season is expected to kick off (epoch ms), if known. */
export function leagueSeasonStartAt(leagueId: string): number | undefined {
  return SEASON_START_AT[leagueId as LeagueKey];
}

/** Short note explaining why a waiting league isn't playable yet. */
export function leagueWaitingNote(leagueId: string): string {
  const start = leagueSeasonStartAt(leagueId);
  if (start == null) return 'New season starts soon';
  const month = new Date(start).toLocaleDateString('en-GB', { month: 'long' });
  return `New season starts ${month}`;
}
